import type React from 'react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@apollo/client';
import useConfig from 'app/config';

import GenericPage from 'app/ui/components/layouts/generic-page';
import WorkoutArchive from 'app/ui/components/molecules/workout-archive';
import LibraryCollections from 'app/ui/components/molecules/library-collections';
import LibraryChallengeCollections from 'app/ui/components/molecules/library-challenge-collections';
import LibraryTrainingPlans from 'src/app/ui/components/molecules/library-training-plans';
import Loading from 'app/ui/components/molecules/loading';
import ErrorComponent from 'app/ui/components/molecules/error';
import { trackEvent } from 'src/app/utils/analytics/tracker';

import { LibraryPage as LIBRARY_PAGE } from 'app/pages/library/query.gql';
import type {
  Collection,
  LibraryPageQuery,
  LibraryPageQueryVariables,
  ProgressivePlanInstance,
} from 'app/types/graphql';

const LibraryPage: React.FC = () => {
  const { config } = useConfig();
  const intl = useIntl();
  const libraryHeader = intl.formatMessage({ id: 'library.header' });
  const progressivePlansEnabled = config.REACT_APP_PROGRESSIVE_PLANS_ENABLED;

  const { data, loading, error } = useQuery<LibraryPageQuery, LibraryPageQueryVariables>(LIBRARY_PAGE, {
    variables: { collectionListId: config.REACT_APP_ROOT_COLLECTION_LIST_ID },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('fromNav') === 'true' ? 'from_nav' : 'home_cta';
    trackEvent('library_opened', { source });
  }, [window.location.search]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ErrorComponent />;
  }

  const planInstances = (data?.progressivePlanInstances?.planInstances ?? []) as ProgressivePlanInstance[];
  const plans = data?.progressivePlans?.nodes ?? [];
  const collections = (data?.collectionList?.collections ?? []) as Collection[];
  const lessonCollections = collections.filter((collection) => !collection.challenges?.length);
  const challengeCollections = collections.filter((collection) => (collection.challenges?.length ?? 0) > 0);

  return (
    <GenericPage pageTitle={libraryHeader}>
      <LibraryCollections collections={lessonCollections} />
      <LibraryChallengeCollections collections={challengeCollections} />
      {progressivePlansEnabled && <LibraryTrainingPlans planInstances={planInstances} plans={plans} />}
      <WorkoutArchive />
    </GenericPage>
  );
};
export default LibraryPage;
